import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import ContributeForm from '../../form/ContributeForm/ContributeForm';
import {
  DEFAULT_GRID_COLUMNS,
  DEFAULT_GRID_ROWHEIGHT,
  GRID_GUTTER,
} from '../../../constants/grid';
import Grid from '../../organisms/Grid/Grid';
import {
  buildFormTree,
  buildAnswer,
  computeComputedHeights,
} from '../../../helpers/formTree';
import { compileAllRules, boxHiddenRule } from '../../../helpers/formula';
import ContributeFormNode from './../ContributeFormNode/ContributeFormNode';
import HiddenBoxesContext from '../ContributeFormTemplate/HiddenBoxesContext';
import ComputedHeightsContext from '../ContributeFormTemplate/ComputedHeightsContext';
import styles from './ContributeAnswerFormTemplate.css';

function ContributeAnswerFormTemplate({ answer, form }) {
  // Build the form tree from blocks & fields
  const formTree = useMemo(() => buildFormTree(form), [form]);

  // Generates initial values according to form tree shape
  const initialValues = useMemo(() => buildAnswer(answer), [answer]);

  // Compile hide rules and evaluate them against the answer values.
  // In readonly mode values never change, so initialValues IS the effective state.
  const hideRules = useMemo(
    () => compileAllRules(formTree.hideRules, formTree),
    [formTree],
  );

  const hiddenBoxes = useMemo(() => {
    const [errors] = boxHiddenRule(hideRules, initialValues);
    return [
      ...new Set(
        errors.reduce((boxes, error) => [error.fieldPath, ...boxes], []),
      ),
    ];
  }, [hideRules, initialValues]);

  // Pre-compute heights
  const computedHeights = useMemo(
    () => computeComputedHeights(formTree, hiddenBoxes, initialValues),
    [formTree, hiddenBoxes, initialValues],
  );

  const formHeight =
    computedHeights.get('root')?.height ?? formTree.totalHeight;

  const gridRowHeight =
    (DEFAULT_GRID_COLUMNS * DEFAULT_GRID_ROWHEIGHT) / form?.gridSize;

  return (
    <ComputedHeightsContext.Provider value={computedHeights}>
      <HiddenBoxesContext.Provider value={hiddenBoxes}>
        <div className={styles.ContributeAnswerFormTemplate}>
          <div
            style={{
              minHeight: formHeight * gridRowHeight + gridRowHeight * 2,
            }}
          >
            <ContributeForm initialValues={initialValues} readonly>
              <Grid
                columns={form.gridSize}
                rowHeight={gridRowHeight}
                gutter={GRID_GUTTER}
                dotBackground={false}
              >
                <ContributeFormNode node={formTree} readonly />
              </Grid>
            </ContributeForm>
          </div>
        </div>
      </HiddenBoxesContext.Provider>
    </ComputedHeightsContext.Provider>
  );
}

ContributeAnswerFormTemplate.propTypes = {
  answer: PropTypes.object,
  form: PropTypes.object,
};

export default ContributeAnswerFormTemplate;
